import React, { useEffect } from "react";
import { View, TouchableOpacity, ScrollView } from "react-native";
import { KeyboardAwareScrollView } from "@/components/ui/KeyboardAvoidingView";
import { Controller } from "react-hook-form";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { AppText } from "@/components/ui/AppText";
import { useRouter } from "expo-router";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/Button";
import { AppTextInput } from "@/components/ui/TextInput";
import { useLoginForm, type LoginMode } from "@/features/auth/hooks/useLoginForm";
import { toast } from "@/lib/toast";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { Image } from "@/components/ui/Image";
import { PageFooter } from "@/components/ui/PageFooter";

const LOGIN_HERO_IMAGE = require("@assets/images/login-background-image.png");

// ─── Form content (shared mobile + web) ──────────────────────────────────────

function LoginForm() {
  const { t } = useI18n();
  const router = useRouter();

  const { mode, switchMode, passwordForm, isLoading, error, handleSendOTP, handlePasswordLogin } =
    useLoginForm();

  const handleSubmit = mode === "password" ? handlePasswordLogin : handleSendOTP;

  useEffect(() => {
    if (!error) return;

    toast.error({
      message: t(error as TranslationKeys),
    });
  }, [error, t]);

  return (
    <View className="items-start w-full">
      <AppText className="text-3xl text-dark-text-50 mb-2" weight="bold">
        {t("auth.login.title")}
      </AppText>
      <AppText className="text-base text-dark-text-15 mb-8">{t("auth.login.subtitle")}</AppText>

      <View className="flex-row mb-6 border p-1 border-primary bg-primary-5 rounded-md overflow-hidden w-full">
        {(["password", "otp"] as LoginMode[]).map((m) => (
          <TouchableOpacity
            key={m}
            onPress={() => switchMode(m)}
            className={`flex-1 py-2  rounded-md items-center ${mode === m ? "bg-white" : ""}`}
          >
            <AppText
              className={`text-sm font-sans-medium ${mode === m ? "text-primary font-sans-semibold" : "text-supporting-text"}`}
            >
              {m === "password" ? t("auth.login.tabPassword") : t("auth.login.tabOtp")}
            </AppText>
          </TouchableOpacity>
        ))}
      </View>

      {/* Email — single source of truth via passwordForm */}
      <Controller
        control={passwordForm.control}
        name="email"
        render={({ field: { value, onChange }, fieldState: { error: fieldError } }) => (
          <AppTextInput
            required
            label="auth.login.emailLabel"
            placeholder="auth.login.emailPlaceholder"
            value={value}
            onChangeText={onChange}
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            error={fieldError?.message as TranslationKeys | undefined}
          />
        )}
      />

      {mode === "password" && (
        <Controller
          control={passwordForm.control}
          name="password"
          render={({ field: { value, onChange }, fieldState: { error: fieldError } }) => (
            <AppTextInput
              required
              label="auth.login.passwordLabel"
              placeholder="auth.login.passwordPlaceholder"
              value={value}
              onChangeText={onChange}
              secureTextEntry
              error={fieldError?.message as TranslationKeys | undefined}
            />
          )}
        />
      )}

      {mode === "password" && (
        <TouchableOpacity
          className="mb-8"
          onPress={() => {
            const email = passwordForm.getValues("email");
            router.push({
              pathname: "/(auth)/forgot-password" as never,
              params: email ? { email } : undefined,
            });
          }}
        >
          <AppText className="text-primary text-sm font-sans-medium">
            {t("auth.login.forgotPassword")}
          </AppText>
        </TouchableOpacity>
      )}

      <Button
        className="w-full"
        label={mode === "otp" ? "auth.login.sendOtp" : "auth.login.submit"}
        onPress={handleSubmit}
        loading={isLoading}
        variant="primary"
      />
    </View>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function LoginScreen() {
  const insets = useSafeAreaInsets();
  const isWide = useIsWideScreen();

  // ── Wide-screen web layout (≥768px): split image + form ────────────────────
  if (isWide) {
    return (
      <View className="flex-1 bg-white min-h-full">
        <View className="flex-1 flex-row">
          {/* Left image panel */}
          <Image
            source={LOGIN_HERO_IMAGE}
            className="flex-1 bg-blue-90 max-w-xs lg:max-w-md"
            contentFit="cover"
          />

          {/* Right scrollable form */}
          <ScrollView
            className="flex-1 bg-white"
            contentContainerStyle={{ flexGrow: 1 }}
            showsVerticalScrollIndicator={false}
          >
            <View className="flex-1 items-center justify-start py-12 px-8">
              <View className="max-w-md w-full">
                <LoginForm />
              </View>
            </View>
            <PageFooter />
          </ScrollView>
        </View>
      </View>
    );
  }

  // ── Mobile layout ────────────────────────────────────────────────────────
  return (
    <KeyboardAwareScrollView
      contentContainerStyle={{ flexGrow: 1, paddingBottom: Math.max(insets.bottom, 24) }}
      className="bg-white py-4 tablet:py-page-y"
    >
      <View className="self-center w-full tablet:max-w-3xl px-page-x">
        <LoginForm />
      </View>
      <PageFooter />
    </KeyboardAwareScrollView>
  );
}
